import styled from "styled-components";
import { Locale } from "@dealer-center/ddc-core-utils";

import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  CheckBox,
  SelectInput
} from "@dealer-center/core-components";
import "./SelectVehicles.scss";
import type { MapTreeNode } from "../utils/navFilterUtils";
import { arrayActivity, getMakeTree, getMakes } from "../utils/navFilterUtils";
import type { IncentivesRecord } from "@dealer-center/ddc-pages-incentives-manager-interface";
import type { RootNode } from "@dealer-center/ddc-pages-incentives-manager-lambda";
import { defaultFilters } from "../providers/FiltersProvider";

const displayExtraMessage = (incentive: IncentivesRecord): boolean => {
  const status = incentive?.accountMap?.status;
  return status ? status.includes("DRAFT") : false;
};

const StyledLabel = styled.div`
  color: #6d6d6d;
  line-height: 1.15;
  font-size: 14px;
  font-weight: 400;
  font-family: "Roboto Flex", Roboto, sans-serif;
  margin: 16px 0 0;
`;
const TopLabel = styled.div`
  color: #6d6d6d;
  line-height: 1.15;
  font-size: 14px;
  font-weight: 400;
  font-family: "Roboto Flex", Roboto, sans-serif;
  margin: 0;
`;
const ConditionExtraMessage = styled.div`
  color: #6d6d6d;
  font-size: 14px;
  font-weight: 350;
  font-style: italic;
  font-family: "Roboto Flex", Roboto, sans-serif;
  margin: -8px 0 0 16px;
`;
const StyledDiv = styled.div`
  margin-left: 8px;
`;
const StyledDiv2 = styled.div`
  margin-left: 16px;
`;
const StyledHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 16px 0 0;
`;

export const formatFilterItems = (items, isExcludingAll = false) => {
  const formattedItems = isExcludingAll
    ? []
    : [
        {
          disabled: false,
          label: Locale.getText("ALL") as string,
          value: "ALL"
        }
      ];
  return formattedItems.concat(
    items.map(item => {
      return {
        disabled: false,
        label: typeof item === "number" ? JSON.stringify(item) : item,
        value: typeof item === "number" ? JSON.stringify(item) : item
      };
    })
  );
};

const showTree = (
  disable: boolean,
  makeTree: MapTreeNode,
  setMakeTree: (makeTree: MapTreeNode) => void
) => {
  if (!makeTree || !makeTree.nodes || makeTree.nodes.length === 0) {
    return null;
  }

  const areAllItemsSelected = (): boolean => {
    return Array.isArray(makeTree.active) && makeTree.active.every(Boolean);
  };

  const handleSelectAll = (selectAll: boolean) => {
    const updateActiveState = node => ({
      ...node,
      active: node.active.map(() => selectAll),
      nodes: node.nodes?.map(updateActiveState)
    });
    setMakeTree(updateActiveState(makeTree));
  };

  return (
    <>
      <StyledHeaderContainer>
        <StyledLabel style={{ margin: 0 }}>
          {Locale.getText("YEAR_MODEL_TRIM")}
        </StyledLabel>
        <CheckBox
          checked={areAllItemsSelected()}
          disabled={disable}
          label="Select All"
          name="select-all-year-model-trim"
          value="select-all"
          onChange={() => {
            const allSelected = areAllItemsSelected();
            handleSelectAll(!allSelected);
          }}
        />
      </StyledHeaderContainer>
      <AccordionGroup>
        {makeTree.nodes.map((modelNode, yearIdx) => (
          <Accordion
            id={"accordion" + makeTree.labels[yearIdx]}
            key={"accordion" + makeTree.labels[yearIdx]}
          >
            <AccordionSummary>
              <CheckBox
                checked={
                  makeTree.active[yearIdx] === null
                    ? undefined
                    : makeTree.active[yearIdx]
                }
                disabled={disable}
                indeterminate={makeTree.active[yearIdx] === null}
                key={"header" + makeTree.labels[yearIdx]}
                label={makeTree.labels[yearIdx]}
                name={"header" + makeTree.labels[yearIdx]}
                value={makeTree.labels[yearIdx]}
                onChange={event => {
                  const isActive = !(makeTree.active[yearIdx] === true);
                  makeTree.active[yearIdx] = isActive;
                  if (
                    makeTree.nodes &&
                    makeTree.nodes[yearIdx] &&
                    makeTree.nodes[yearIdx].nodes
                  ) {
                    makeTree.nodes[yearIdx].active = makeTree.nodes[
                      yearIdx
                    ].active.map(() => isActive);
                    makeTree.nodes[yearIdx].nodes.forEach(
                      (trimNode, modelIdx) => {
                        trimNode.active = trimNode.active.map(() => isActive);
                      }
                    );
                    setMakeTree({ ...makeTree });
                  }
                }}
              />
            </AccordionSummary>
            <AccordionDetails>
              <StyledDiv>
                {modelNode.nodes?.map((trimNode, modelIdx) => {
                  return (
                    <Accordion
                      id={makeTree.labels[yearIdx] + modelNode.labels[modelIdx]}
                      key={
                        makeTree.labels[yearIdx] + modelNode.labels[modelIdx]
                      }
                    >
                      <AccordionSummary>
                        <CheckBox
                          checked={
                            modelNode.active[modelIdx] === null
                              ? undefined
                              : modelNode.active[modelIdx]
                          }
                          disabled={disable}
                          indeterminate={modelNode.active[modelIdx] === null}
                          key={modelNode.labels[modelIdx]}
                          label={modelNode.labels[modelIdx]}
                          name={modelNode.labels[modelIdx]}
                          value={modelNode.labels[modelIdx]}
                          onChange={event => {
                            const isActive = !(
                              modelNode.active[modelIdx] === true
                            );
                            modelNode.active[modelIdx] = isActive;
                            if (modelNode.nodes && modelNode.nodes.length > 0) {
                              trimNode.active = trimNode.active.map(
                                () => isActive
                              );
                            }

                            const { isAllActive, isNoneActive } =
                              arrayActivity(modelNode);
                            makeTree.active[yearIdx] = isAllActive
                              ? true
                              : isNoneActive
                                ? false
                                : null;

                            setMakeTree({ ...makeTree });
                          }}
                        />
                      </AccordionSummary>
                      <AccordionDetails>
                        <StyledDiv2>
                          {trimNode.labels.map((trimLabel, trimIdx) => (
                            <CheckBox
                              checked={
                                trimNode.active[trimIdx] === null
                                  ? undefined
                                  : trimNode.active[trimIdx]
                              }
                              disabled={disable}
                              id={
                                makeTree.labels[yearIdx] +
                                modelNode.labels[modelIdx] +
                                trimNode.labels[trimIdx]
                              }
                              key={
                                makeTree.labels[yearIdx] +
                                modelNode.labels[modelIdx] +
                                trimNode.labels[trimIdx]
                              }
                              label={trimLabel}
                              name={
                                makeTree.labels[yearIdx] +
                                modelNode.labels[modelIdx] +
                                trimNode.labels[trimIdx]
                              }
                              value={trimLabel}
                              onChange={event => {
                                const isActive = !(
                                  trimNode.active[trimIdx] === true
                                );
                                trimNode.active[trimIdx] = isActive;

                                const { isAllActive, isNoneActive } =
                                  arrayActivity(trimNode);
                                modelNode.active[modelIdx] = isAllActive
                                  ? true
                                  : isNoneActive
                                    ? false
                                    : null;
                                const {
                                  isAllActive: isAllModelsActive,
                                  isSomeActive: isSomeModelsActive,
                                  nNulls: nModelsNull
                                } = arrayActivity(modelNode);
                                makeTree.active[yearIdx] = isAllModelsActive
                                  ? true
                                  : isSomeModelsActive || nModelsNull
                                    ? null
                                    : false;
                                setMakeTree({ ...makeTree });
                              }}
                            />
                          ))}
                        </StyledDiv2>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </StyledDiv>
            </AccordionDetails>
          </Accordion>
        ))}
      </AccordionGroup>
    </>
  );
};

export const SelectVehicles = ({
  incentive,
  setIncentive,
  condition,
  isMakeLocked,
  makeTree,
  navtreeData,
  setMakeTree,
  makeRequired = false,
  disableMake = false,
  type,
  disableYearModelTrim = false
}: {
  incentive: IncentivesRecord;
  setIncentive: (
    updater: (prevState: IncentivesRecord) => IncentivesRecord
  ) => void;
  condition: string;
  isMakeLocked?: (make: string) => boolean | undefined;
  makeTree: MapTreeNode;
  navtreeData: RootNode | null;
  setMakeTree: (makeTree: MapTreeNode) => void;
  makeRequired?: boolean;
  disableMake?: boolean;
  type?: string;
  disableYearModelTrim?: boolean;
}) => {
  const vehicleConditions = [
    ...(!type
      ? [
          {
            disabled: false,
            label: Locale.getText("ALL") as string,
            value: "ALL"
          }
        ]
      : []),
    {
      disabled: false,
      label: Locale.getText("NEW") as string,
      value: "NEW"
    },
    {
      disabled: false,
      label: Locale.getText("CERTIFIED_INVENTORY") as string,
      value: "CPO"
    }
  ];

  return (
    <div className="select-vehicles">
      <div className="navigation-items">
        <TopLabel>
          {Locale.getText("VEHICLE_CONDITION")}:{" "}
          {type === "CREATE_INCENTIVE"
            ? Locale.getText("NEW")
            : (() => {
                const currentCondition =
                  condition || incentive.applicableCondition || "NEW";
                return currentCondition === "CPO" ||
                  currentCondition === "Certified Pre-Owned"
                  ? Locale.getText("IM_CERTIFIED_PRE_OWNED")
                  : Locale.getText("NEW");
              })()}
        </TopLabel>
        {displayExtraMessage(incentive) && (
          <ConditionExtraMessage>
            {Locale.getText("CONDITION_EXTRA_MESSAGE")}
          </ConditionExtraMessage>
        )}
        <SelectInput
          disabled={disableMake}
          displayDeselectOption={false}
          id={`for-nav-make${makeRequired ? "-extra" : ""}`}
          label={Locale.getText("MAKE") as string}
          name={Locale.getText("MAKE") as string}
          options={formatFilterItems(
            getMakes(
              navtreeData,
              { ...defaultFilters, make: incentive.applicableMake },
              make => {
                if (isMakeLocked) {
                  return isMakeLocked(incentive.applicableMake);
                }
                return false;
              }
            ),
            makeRequired
          )}
          value={incentive.applicableMake ? incentive.applicableMake : "ALL"}
          onChange={event => {
            const make = event.target.value || "";
            setIncentive((prevState: IncentivesRecord) => ({
              ...prevState,
              applicableMake: make
            }));
            setMakeTree(getMakeTree(navtreeData, make, false));
          }}
        />
        {showTree(disableYearModelTrim, makeTree, setMakeTree)}
      </div>
    </div>
  );
};
